import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAccountPreferences, useUpdateAccountPreferences } from "@/queries/account";
import { Rows3, Table2, PanelsTopLeft } from "lucide-react";
import { toast } from "sonner";
import { getNativePlatform } from "@/lib/runtime";
import { NativeSettingsSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

function ViewPreview({ active, disabled, icon: Icon, title, text, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-active={active ? "true" : "false"}
      className={`native-v3-protocol-view-option w-full text-left transition-colors ${
        disabled ? "cursor-not-allowed opacity-70" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
        <div className="font-medium">{title}</div>
        {active ? <Badge variant="secondary">Aktivt</Badge> : null}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
    </button>
  );
}

export default function ProtocolDefaultViewSettingCard() {
  const { data, isLoading } = useAccountPreferences();
  const updatePreferences = useUpdateAccountPreferences();
  const serverView = useMemo(
    () => (data?.preferences?.protocol_default_view === "table" ? "table" : "cards"),
    [data?.preferences?.protocol_default_view]
  );
  const [defaultView, setDefaultView] = useState(() => serverView);
  const activeView = updatePreferences.isPending ? defaultView : serverView;

  useEffect(() => {
    if (updatePreferences.isPending) return;
    setDefaultView(serverView);
  }, [serverView, updatePreferences.isPending]);

  if (isLoading && getNativePlatform() === "android") {
    return <NativeSettingsSkeleton sections={1} />;
  }

  const setView = async (nextView) => {
    if (nextView === defaultView || updatePreferences.isPending) return;

    setDefaultView(nextView);
    try {
      await toast.promise(
        updatePreferences.mutateAsync({
          preferences: {
            auto_scroll_next_heat: data?.preferences?.auto_scroll_next_heat !== false,
            allow_haptics: data?.preferences?.allow_haptics !== false,
            haptic_intensity: data?.preferences?.haptic_intensity || "normal",
            protocol_default_view: nextView,
            protocol_default_view_confirmed: true,
          },
        }),
        {
          loading: "Sparar inställning...",
          success: nextView === "table" ? "Tabellvy satt som standard" : "Heatkort satt som standard",
          error: (error) => error?.message || "Kunde inte spara inställningen",
        }
      );
    } catch {
      setDefaultView((current) => (current === "table" ? "cards" : "table"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PanelsTopLeft className="h-5 w-5 shrink-0 opacity-70" />
          <span>Standardvy för matchprotokoll</span>
        </CardTitle>
        <CardDescription>
          Välj vilken vy som ska öppnas först när du startar eller återupptar ett matchprotokoll.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <ViewPreview
            active={activeView === "cards"}
            disabled={isLoading || updatePreferences.isPending}
            icon={Rows3}
            title="Heatkort"
            text="Öppna protokollet i standardvyn med ett heatkort i taget."
            onClick={() => setView("cards")}
          />
          <ViewPreview
            active={activeView === "table"}
            disabled={isLoading || updatePreferences.isPending}
            icon={Table2}
            title="Tabellvy"
            text="Öppna protokollet i den sammanfattande tabellvyn direkt."
            onClick={() => setView("table")}
          />
        </div>
      </CardContent>
    </Card>
  );
}
